/**
 * Destructive-action confirmation dialog.
 *
 * Backend gates DELETE / publish / refund / extend / cancel via a
 * short-lived challenge token issued by POST /auth/totp/challenge.
 * When the admin has TOTP disabled (default), the backend issues the
 * token without verifying any code — the in-app "Are you sure?" dialog
 * IS the gate. When the admin opts in to 2FA, the same dialog will
 * also prompt for a 6-digit code (TODO when 2FA is reactivated).
 *
 * Public API kept identical (`useTotpChallenge`) so existing call
 * sites in BumpsPage / CouponsPage / etc. work unchanged.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@iptv/ui';
import { ShieldAlert } from 'lucide-react';
import { useCallback, useState } from 'react';

import { adminFetch } from './api';

interface ChallengeState {
  open: boolean;
  message: string;
  resolver?: (token: string | null) => void;
}

interface ChallengeRequest {
  message?: string;
}

let setStateRef: React.Dispatch<React.SetStateAction<ChallengeState>> | null = null;

export function TotpChallengeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ChallengeState>({ open: false, message: '' });
  setStateRef = setState;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setError(null);
    setLoading(true);
    try {
      // Backend issues a valid challenge token without verifying any code
      // when the admin has TOTP disabled. If 2FA is opted in later, this
      // call will need to send a real 6-digit code.
      const res = await adminFetch<{ challenge_token: string }>('/api/v1/auth/totp/challenge', {
        method: 'POST',
        body: { code: '' },
      });
      state.resolver?.(res.challenge_token);
      setState({ open: false, message: '' });
    } catch (err) {
      // FastAPI validation errors return `detail` as an array of objects
      // rather than a plain string, so we coerce defensively.
      const raw = (err as { detail?: unknown }).detail;
      let message = 'Confirmation failed';
      if (typeof raw === 'string') {
        message = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        const first = raw[0] as { msg?: string };
        message = first?.msg ?? message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  function cancel() {
    state.resolver?.(null);
    setState({ open: false, message: '' });
    setError(null);
  }

  return (
    <>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && cancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Are you sure?
            </DialogTitle>
            <DialogDescription>{state.message}</DialogDescription>
          </DialogHeader>
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={cancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirm} disabled={loading}>
              {loading ? 'Working…' : 'Yes, do it'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Pop a confirmation dialog and resolve to a challenge token (or null
 * if the operator cancels). The provider must be mounted higher in
 * the tree.
 */
export function useTotpChallenge() {
  return useCallback(async (request: ChallengeRequest = {}): Promise<string | null> => {
    if (!setStateRef) {
      console.warn('TotpChallengeProvider not mounted');
      return null;
    }
    return new Promise<string | null>((resolve) => {
      setStateRef!({
        open: true,
        message: request.message ?? 'This action cannot be undone. Continue?',
        resolver: resolve,
      });
    });
  }, []);
}
